import { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import store from "../../store";
import { scoreActions } from "../../store/score";
import { validateScore } from "../../lib/social/validation";
import { platform } from "../platform/react";
import { startExtension, SHOW_PANEL } from "./runtime";
import { panelSource } from "./guest";
import styles from "../market/market.module.css";

export const EXTENSIONS_CHANGED = "truechops:extensions";
export const extensionsChanged = () => window.dispatchEvent(new Event(EXTENSIONS_CHANGED));
const extensionRequest = async (path, options) => { const response = await fetch(`/api/extensions/${path}`, { credentials: "same-origin", ...options }); const data = await response.json(); if (!response.ok) throw new Error(data.error || "That didn't work."); return data; };

// What an extension may ask of the page, once runtime.js has checked it's allowed.
export const pageBridge = (showToast) => ({
  toast: (pack, message) => showToast(`${pack.name}: ${message}`),
  "score.get": () => { const { score, name, tempo } = store.getState().score.present; return JSON.parse(JSON.stringify({ score, name, tempo })); },
  // A change is one step of the Score's undo, and has to be a score that's right.
  "score.set": (pack, change = {}) => {
    const present = store.getState().score.present, { score } = validateScore(change.score);
    store.dispatch(scoreActions.updateScore({ score, name: change.name ?? present.name, tempo: Number(change.tempo) || present.tempo, mutations: [], refs: [] }));
    return null;
  },
  "storage.get": async (pack, key) => (await extensionRequest(`${encodeURIComponent(pack.id)}/data/${encodeURIComponent(key)}`)).value,
  "storage.set": async (pack, key, value) => { await extensionRequest(`${encodeURIComponent(pack.id)}/data/${encodeURIComponent(key)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value }) }); return null; },
});

// Keeps what someone has added running: official extensions show in their menu, and community ones
// start in their sandboxes. Signing in or out, or adding or taking one off, starts it over.
export default function ExtensionHost() {
  const user = useSelector((state) => state.realm.currentUser), loaded = useSelector((state) => state.realm.sessionLoaded), running = useRef(new Map());
  const [toast, setToast] = useState(""), [panel, setPanel] = useState(null), timer = useRef(null), frame = useRef(null);
  const showToast = (text) => { setToast(text); clearTimeout(timer.current); timer.current = setTimeout(() => setToast(""), 5000); };
  useEffect(() => {
    if (!loaded) return undefined;
    let current = true;
    const load = async () => {
      const site = platform();
      try {
        const { official, packages } = await extensionRequest("installed");
        if (!current) return;
        site.setInstalled(official);
        const wanted = new Map(packages.map((pack) => [pack.id, pack]));
        for (const [id, item] of running.current) if (wanted.get(id)?.version !== item.version) { item.stop(); running.current.delete(id); }
        for (const pack of packages) if (!running.current.has(pack.id)) {
          const started = startExtension(site, pack, { bridge: pageBridge(showToast), say: ({ level, text }) => { if (level === "error") showToast(text); else console.info(`[${pack.name}]`, text); } });
          running.current.set(pack.id, { ...started, version: pack.version });
        }
      } catch { /* Without them, the site is as it comes. */ }
    };
    load();
    window.addEventListener(EXTENSIONS_CHANGED, load);
    return () => { current = false; window.removeEventListener(EXTENSIONS_CHANGED, load); };
  }, [loaded, user?.id]);
  useEffect(() => {
    const show = (event) => setPanel(event.detail);
    window.addEventListener(SHOW_PANEL, show);
    return () => window.removeEventListener(SHOW_PANEL, show);
  }, []);
  // A panel's messages go to its extension, from its own frame only.
  useEffect(() => {
    if (!panel) return undefined;
    const hear = (event) => { if (event.source === frame.current?.contentWindow && event.data?.type === "panel") panel.send(event.data.data).catch(() => {}); };
    window.addEventListener("message", hear);
    return () => window.removeEventListener("message", hear);
  }, [panel]);
  return <>
    {toast && <div className={styles.toast} role="status">{toast}</div>}
    {panel && <aside className={styles.panel} aria-label={panel.title}>
      <header><strong>{panel.title}</strong><small>{panel.pack.name}</small><button type="button" aria-label="Close" onClick={() => setPanel(null)}><FiX aria-hidden="true" /></button></header>
      <iframe ref={(element) => { frame.current = element; if (element) panel.attach(element); }} title={panel.title} sandbox="allow-scripts" srcDoc={panelSource(panel.html)} />
    </aside>}
  </>;
}
